/**
 * WorkspaceInvitationModal (Figma MVP-DESIGNES 10216:3390, with the
 * `1/2 invitations` counter 10219:22067).
 *
 * A 400px Spark Modal without a header: the inviting workspace's logo (or
 * initials) as a 100px hero, `Join {workspace}`, who invited the person and
 * as which role, then Decline / Accept sharing the footer.  Escape and the
 * overlay call `onClose`, which the owner treats as "decide later" (the
 * invitation becomes a Pending row in the avatar menu).
 *
 * The last invitation is kept while the portal fades out so the dialog does
 * not blank before it disappears.
 */
import React, { useRef } from 'react';
import { Modal, SparkModalPortal, Text } from '@spark';
import { WorkspaceAvatar } from './WorkspaceSwitcher';
import './WorkspaceInvitationModal.css';

export const WORKSPACE_INVITATION_MODAL_WIDTH = 400;
export const INVITATION_ACCEPT_LABEL = 'Accept';
export const INVITATION_DECLINE_LABEL = 'Decline';

const STARTS_WITH_VOWEL = /^[aeiou]/i;

/** `as an Editor` / `as a Viewer`. */
export const roleWithArticle = (role) => {
    const name = String(role || '').trim();
    if (!name) return 'a member';
    return `${STARTS_WITH_VOWEL.test(name) ? 'an' : 'a'} ${name}`;
};

/** `Shani (shani@workplace.com)`, or just the address when the inviter has no display name. */
export const inviterLabel = ({ inviter_name: name, inviter_email: email } = {}) => {
    const who = String(name || '').trim();
    const address = String(email || '').trim();
    if (who && address) return `${who} (${address})`;
    return who || address || 'A workspace admin';
};

export const invitationCounterLabel = (position, total) => `${position}/${total} invitations`;

export default function WorkspaceInvitationModal({
    open = false,
    invitation = null,
    position = 1,
    total = 1,
    /** The decision in flight, `'accept'` | `'decline'` | null: that button spins, the other is disabled. */
    busyAction = null,
    onAccept,
    onDecline,
    onClose,
}) {
    const lastRef = useRef(invitation);
    if (invitation) lastRef.current = invitation;
    const shown = invitation || lastRef.current;
    if (!shown) return null;

    const workspaceName = shown.workspace_name || 'this workspace';
    const title = `Join ${workspaceName}`;
    const accepting = busyAction === 'accept';
    const declining = busyAction === 'decline';

    return (
        <SparkModalPortal
            open={open}
            onClose={() => onClose?.()}
            width={WORKSPACE_INVITATION_MODAL_WIDTH}
            className="workspace-invitation-modal-portal"
            destroyOnClose
        >
            <Modal
                open
                className="workspace-invitation-modal"
                showHeader={false}
                ariaLabel={title}
                showOverlay={false}
                showScrollBar={false}
                fixedHeight={false}
                footerLayout="stretch"
                tertiaryLabel={INVITATION_DECLINE_LABEL}
                tertiaryLoading={declining}
                tertiaryDisabled={accepting}
                primaryLabel={INVITATION_ACCEPT_LABEL}
                primaryLoading={accepting}
                primaryDisabled={declining}
                onTertiaryClick={() => onDecline?.()}
                onPrimaryClick={() => onAccept?.()}
                onCloseClick={() => onClose?.()}
            >
                <div className="workspace-invitation-modal__content" data-testid="workspace-invitation-modal">
                    <div className="workspace-invitation-modal__top">
                        {total > 1 ? (
                            <Text
                                as="p"
                                variant="base-regular"
                                color="secondary"
                                className="workspace-invitation-modal__counter"
                                data-testid="workspace-invitation-counter"
                            >
                                {invitationCounterLabel(position, total)}
                            </Text>
                        ) : null}
                        <div className="workspace-invitation-modal__hero">
                            <WorkspaceAvatar
                                workspace={{ name: workspaceName, logo: shown.workspace_logo }}
                                size="xlarge"
                            />
                            <Text as="h2" variant="lead-bold" color="secondary" className="workspace-invitation-modal__title">
                                {title}
                            </Text>
                        </div>
                    </div>
                    <Text as="p" variant="base-regular" color="secondary" className="workspace-invitation-modal__body">
                        {inviterLabel(shown)}
                        <br />
                        has invited you to join as {roleWithArticle(shown.role)}.
                    </Text>
                </div>
            </Modal>
        </SparkModalPortal>
    );
}
